import React, { useEffect, useState } from 'react';
import PurchaseHeader from './components/PurchaseHeader';
import PurchaseDetailsForm from './components/PurchaseDetailsForm';
import SupplierDetailsForm from './components/SupplierDetailsForm';
import DepotSelector from '../components/DepotSelector';
import { usePurchaseForm } from './viewmodel/usePurchaseForm';
import { useNavigate, useParams } from 'react-router-dom';
import AddPurchaseItemsTable from './components/AddPurchaseItemsTable';
import PurchaseItemsTable from './components/PurchaseItemsTable'; // Import the edit version
import OrderSummary from '../new-order-components/OrderSummary';

const Purchase = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Use the hook with the correct edit mode parameter
  const {
    formData,
    purchaseItems,
    setPurchaseItems,
    totals,
    errors,
    isLoading,
    handleInputChange,
    handleAddItem: hookHandleAddItem,
    handleUpdateItem: hookHandleUpdateItem,
    handleRemoveItem: hookHandleRemoveItem,
    handleSubmit,
    updateTotals
  } = usePurchaseForm(navigate, isEditMode);


  // Only use local state for create mode, not for edit mode
  const [localItems, setLocalItems] = useState([]);

  // Add this debugging function at the top of your component
  const debugCalculateTotals = (items) => {
    console.log("=== DEBUG TOTALS CALCULATION ===");
    console.log("Items to calculate:", items);
    
    if (!items || items.length === 0) {
      console.log("No items to calculate");
      return { subtotal: 0, totalTax: 0, totalDiscount: 0, total: 0 };
    }
    
    let subtotal = 0;
    let totalTax = 0;
    let totalDiscount = 0;
    
    items.forEach((item, index) => {
      const quantity = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.prix) || parseFloat(item.price) || 0;
      const discount = parseFloat(item.discount) || parseFloat(item.remise) || 0;
      const taxRate = parseFloat(item.taxRate) || parseFloat(item.tva) || 0;
      
      console.log(`Item ${index}:`, {
        quantity,
        price,
        discount,
        taxRate,
        item
      });
      
      // Calculate line total before discount
      const lineTotal = quantity * price;
      console.log(`Line total (qty * price): ${lineTotal}`);
      
      // Calculate discount amount
      const discountAmount = (lineTotal * discount) / 100;
      console.log(`Discount amount: ${discountAmount}`);
      
      // Calculate subtotal after discount
      const lineSubtotal = lineTotal - discountAmount;
      console.log(`Line subtotal (after discount): ${lineSubtotal}`);
      
      // Calculate tax amount
      const taxAmount = (lineSubtotal * taxRate) / 100;
      console.log(`Tax amount: ${taxAmount}`);
      
      subtotal += lineSubtotal;
      totalTax += taxAmount;
      totalDiscount += discountAmount;
    });
    
    const total = subtotal + totalTax;
    
    const result = {
      subtotal: Number(subtotal.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      totalDiscount: Number(totalDiscount.toFixed(2)),
      total: Number(total.toFixed(2))
    };
    
    console.log("Final calculation result:", result);
    console.log("=== END DEBUG CALCULATION ===");
    
    return result;
  };

  // Handle add item based on mode
  // Fix the handleAddItem function to properly accumulate items
  // Fix the handleAddItem function in Purchase.jsx
const handleAddItem = (newItem) => {
  console.log("Adding item to order:", newItem);
  console.log("New item quantity from modal:", newItem.quantity);
  console.log("Current purchaseItems before adding:", purchaseItems);
  
  // Use functional update to ensure we're working with the latest state
  setPurchaseItems(currentItems => {
    console.log("Current items in setState:", currentItems);
    
    // Check if the item already exists in the current items array
    const itemExists = currentItems.some(item => 
      (item.id === newItem.id) || 
      (item.variantId && newItem.variantId && item.variantId === newItem.variantId)
    );
    
    let updatedItems;
    
    if (itemExists) {
      console.log("Item already exists in the order:", newItem.id);
      console.log("Adding quantity:", newItem.quantity, "to existing item");
      // Update the quantity instead of replacing
      updatedItems = currentItems.map(item => 
        ((item.id === newItem.id) || 
         (item.variantId && newItem.variantId && item.variantId === newItem.variantId))
          ? { 
              ...item, 
              quantity: (item.quantity || 0) + (newItem.quantity || 0) // ✅ Don't default to 1
            }
          : item
      );
    } else {
      console.log("Adding new item to order with quantity:", newItem.quantity);
      // Add the new item to the existing array, preserving the exact quantity
      updatedItems = [...currentItems, {
        ...newItem,
        quantity: newItem.quantity || 1 // ✅ Only default to 1 for completely new items
      }];
    }
    
    console.log("Updated items array:", updatedItems);
    console.log("Number of items in array:", updatedItems.length);
    
    // Verify the quantity was preserved
    const addedItem = updatedItems.find(item => 
      (item.id === newItem.id) || 
      (item.variantId && newItem.variantId && item.variantId === newItem.variantId)
    );
    console.log("Final item quantity:", addedItem?.quantity);
    
    // Force the updateTotals function with the new items
    setTimeout(() => {
      console.log("Calling updateTotals with items:", updatedItems);
      updateTotals(updatedItems);
    }, 50);
    
    return updatedItems;
  });
};

  // Add useEffect to automatically recalculate totals when items change
  useEffect(() => {
    console.log("=== USEEFFECT TRIGGERED ===");
    console.log("purchaseItems:", purchaseItems);
    console.log("localItems:", localItems);
    console.log("isEditMode:", isEditMode);
    
    const currentItems = isEditMode ? purchaseItems : (purchaseItems.length > 0 ? purchaseItems : localItems);
    console.log("currentItems for calculation:", currentItems);
    
    if (currentItems && currentItems.length > 0) {
      console.log("Calling updateTotals from useEffect with items:", currentItems);
      
      // Debug calculation
      const debugTotals = debugCalculateTotals(currentItems);
      console.log("Debug totals from useEffect:", debugTotals);
      
      updateTotals(currentItems);
    } else {
      console.log("No items in useEffect, skipping calculation");
    }
  }, [purchaseItems, localItems, isEditMode]); // Remove updateTotals from dependencies

  // Update the handleUpdateItem function to call updateTotals
  const handleUpdateItem = (id, updatedItem) => {
    console.log(`Purchase component: Updating item ${id} with:`, updatedItem);
    
    if (isEditMode) {
      // In edit mode, use the hook's handler directly
      if (typeof hookHandleUpdateItem === 'function') {
        console.log("Calling hook's handleUpdateItem");
        hookHandleUpdateItem(id, updatedItem);
        
        // Force total recalculation after update
        setTimeout(() => {
          const currentItems = purchaseItems.map(item => 
            item.id === id ? { ...item, ...updatedItem } : item
          );
          console.log("Recalculating totals after item update");
          updateTotals(currentItems);
        }, 50);
      } else {
        console.error("hookHandleUpdateItem is not a function");
      }
    } else {
      // In create mode
      if (typeof hookHandleUpdateItem === 'function') {
        hookHandleUpdateItem(id, updatedItem);
        
        // Force total recalculation
        setTimeout(() => {
          const currentItems = purchaseItems.map(item => 
            item.id === id ? { ...item, ...updatedItem } : item
          );
          console.log("Recalculating totals after hook update");
          updateTotals(currentItems);
        }, 50);
      } else {
        // Fall back to local state if hook handler isn't working
        const updatedItems = localItems.map(item => 
          item.id === id ? { ...item, ...updatedItem } : item
        );
        setLocalItems(updatedItems);
        
        // Force total recalculation with local items
        setTimeout(() => {
          console.log("Recalculating totals after local update");
          updateTotals(updatedItems);
        }, 50);
      }
    }
  };

  // Handle remove item based on mode
  const handleRemoveItem = (id) => {
    console.log("Removing item:", id);
    
    if (isEditMode) {
      // In edit mode, use the hook's handler directly
      hookHandleRemoveItem(id);
      
      // Force total recalculation after removal
      setTimeout(() => {
        const remainingItems = purchaseItems.filter(item => item.id !== id);
        console.log("Recalculating totals after item removal");
        updateTotals(remainingItems);
      }, 50);
    } else {
      // In create mode, check if hook handler works or use local state
      if (typeof hookHandleRemoveItem === 'function') {
        hookHandleRemoveItem(id);
        
        // Force total recalculation
        setTimeout(() => {
          const remainingItems = purchaseItems.filter(item => item.id !== id);
          console.log("Recalculating totals after hook removal");
          updateTotals(remainingItems);
        }, 50);
      } else {
        // Fall back to local state
        const remainingItems = localItems.filter(item => item.id !== id);
        setLocalItems(remainingItems);
        
        // Force total recalculation with remaining local items
        setTimeout(() => {
          console.log("Recalculating totals after local removal");
          updateTotals(remainingItems);
        }, 50);
      }
    }
  };

  // Custom submit handler
  const handleCustomSubmit = (e) => {
    e.preventDefault();
    
    // Only need special handling for create mode
    if (!isEditMode && (!purchaseItems || purchaseItems.length === 0) && localItems.length > 0) {
      console.log("Create mode: Hook's purchaseItems is empty, but localItems has data. Updating hook state.");
      
      if (typeof setPurchaseItems === 'function') {
        setPurchaseItems(localItems);
        
        // Small delay for state update
        setTimeout(() => {
          handleSubmit(e);
        }, 100);
        return;
      }
    }
    
    // Otherwise proceed normally
    handleSubmit(e);
  };

  // Also add this manual calculation button temporarily for debugging
  const forceCalculation = () => {
    const currentItems = isEditMode ? purchaseItems : (purchaseItems.length > 0 ? purchaseItems : localItems);
    console.log("FORCE CALCULATION - Current items:", currentItems);
    
    if (currentItems && currentItems.length > 0) {
      const debugTotals = debugCalculateTotals(currentItems);
      console.log("FORCE CALCULATION - Debug totals:", debugTotals);
      updateTotals(currentItems);
    }
  };

  useEffect(() => {
    document.title = isEditMode ? 'Modifier un achat' : 'Nouvel achat';
  }, [isEditMode]);

  return (
    <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <PurchaseHeader isEditMode={isEditMode} />

      <form onSubmit={handleCustomSubmit}>
        <div className="space-y-6">
          {/* Purchase reference and date */}
          <PurchaseDetailsForm 
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
          />
          
          {/* Supplier section */}
          <SupplierDetailsForm 
            formData={formData}
            onChange={handleInputChange}
            errors={errors}
          />

          {/* Depot selector */}
          <div className="bg-white shadow rounded-md px-4 py-5 sm:px-6">
            <DepotSelector
              value={formData.depotId}
              onChange={handleInputChange}
              error={errors.depotId}
            />
          </div>
          
          {/* Items table section - use different components for edit vs create */}
          {isEditMode ? (
            <PurchaseItemsTable
              items={purchaseItems}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onRemoveItem={handleRemoveItem}
              totals={totals}
              onUpdateTotals={updateTotals}
            />
          ) : (
            <AddPurchaseItemsTable
              items={purchaseItems.length > 0 ? purchaseItems : localItems}
              onAddItem={handleAddItem}
              onUpdateItem={handleUpdateItem}
              onRemoveItem={handleRemoveItem}
              totals={totals}
              onUpdateTotals={updateTotals}
            />
          )}
          
          {/* Purchase summary section */}
          <OrderSummary 
            items={isEditMode ? purchaseItems : (purchaseItems.length > 0 ? purchaseItems : localItems)}
            totals={totals} // Pass totals directly without modification
          />
          
          {/* Action buttons */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/admin/purchases')}
              className="inline-flex justify-center py-2.5 px-6 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
              disabled={isLoading}
            >
              {isLoading ? 'Chargement...' : isEditMode ? 'Mettre à jour' : 'Créer l\'achat'}
            </button>
          </div>
        </div>
      </form>

      
    </div>
  );
};

export default Purchase;